/** Structural match for `SegmentTabItem` — kept free of UI imports in domain. */
export interface AnalyticsSegmentTabItem {
  readonly id: string;
  readonly label: string;
}

export type AnalyticsTab =
  | 'kiosk-product-performance'
  | 'funnel'
  | 'payments'
  | 'transactions'
  | 'ingest-quality'
  | 'event-governance'
  | 'advanced-explore'
  | 'advanced-benchmark'
  | 'revenue-summary'
  | 'revenue-kiosks'
  | 'revenue-timeseries'
  | 'revenue-categories'
  | 'revenue-products';

export const DEFAULT_TAB: AnalyticsTab = 'revenue-summary';

export type AnalyticsGroup = 'revenue' | 'operations' | 'quality' | 'advanced';

export const ANALYTICS_GROUPS: readonly AnalyticsGroup[] = [
  'revenue',
  'operations',
  'quality',
  'advanced',
];

export const REVENUE_TAB_IDS: readonly AnalyticsTab[] = [
  'revenue-summary',
  'revenue-kiosks',
  'revenue-timeseries',
  'revenue-categories',
  'revenue-products',
];

export const OPERATIONS_TAB_IDS: readonly AnalyticsTab[] = [
  'kiosk-product-performance',
  'payments',
  'funnel',
  'transactions',
];

/** Quality group — ingest-quality + event-governance (Wave-0: preserve both IDs for URL compat). */
export const QUALITY_TAB_IDS: readonly AnalyticsTab[] = [
  'ingest-quality',
  'event-governance',
];

export const ADVANCED_TAB_IDS: readonly AnalyticsTab[] = [
  'advanced-explore',
  'advanced-benchmark',
];

export const GROUP_TAB_IDS: Record<AnalyticsGroup, readonly AnalyticsTab[]> = {
  revenue: REVENUE_TAB_IDS,
  operations: OPERATIONS_TAB_IDS,
  quality: QUALITY_TAB_IDS,
  advanced: ADVANCED_TAB_IDS,
};

/** Flat union of all analytics tabs (13) — header titles, URL parsing, redirects. */
export const ALL_ANALYTICS_TAB_IDS: readonly AnalyticsTab[] = [
  ...REVENUE_TAB_IDS,
  ...OPERATIONS_TAB_IDS,
  ...QUALITY_TAB_IDS,
  ...ADVANCED_TAB_IDS,
];

/**
 * Primary chrome tabs (Shopify / Stripe Dashboard–style IA).
 * Thin duplicate screens stay in `ALL_ANALYTICS_TAB_IDS` for URL compatibility,
 * redirect via `resolveCanonicalAnalyticsTab`, and stay hidden from the strip.
 *
 * | Intent | Tab id | Content |
 * |--------|--------|---------|
 * | Overview | revenue-summary | KPI + chart grid |
 * | Sales | revenue-timeseries | Trend + categories + products |
 * | Locations | revenue-kiosks | Kiosk table + kiosk×product |
 * | Conversion | funnel | Funnel dashboard |
 * | Payments | payments | Payment mix |
 * | Transactions | transactions | Tx list |
 * | Data quality | event-governance | Catalog + ingest |
 * | Explore / Benchmark | advanced-* | Advanced tools |
 */
export const PRIMARY_NAV_TAB_IDS: readonly AnalyticsTab[] = [
  'revenue-summary',
  'revenue-timeseries',
  'revenue-kiosks',
  'funnel',
  'payments',
  'transactions',
  'event-governance',
  'advanced-explore',
  'advanced-benchmark',
];

/** Legacy / thin-tab URLs → canonical primary screen. */
export const ANALYTICS_TAB_ALIASES: Readonly<Partial<Record<AnalyticsTab, AnalyticsTab>>> = {
  'revenue-categories': 'revenue-timeseries',
  'revenue-products': 'revenue-timeseries',
  'kiosk-product-performance': 'revenue-kiosks',
  'ingest-quality': 'event-governance',
};

export function resolveCanonicalAnalyticsTab(tab: AnalyticsTab): AnalyticsTab {
  return ANALYTICS_TAB_ALIASES[tab] ?? tab;
}

/**
 * Tabs that render product / sales-point mix charts (composed Sales + Locations).
 * Must stay in sync with panel mounts — missing IDs cause permanent empty states.
 */
export function tabNeedsProductPerformance(tab: AnalyticsTab): boolean {
  return (
    tab === 'kiosk-product-performance' ||
    tab === 'revenue-products' ||
    tab === 'revenue-kiosks' ||
    tab === 'revenue-timeseries'
  );
}

/** Tabs that need commerce daily rollup for sales-point revenue join. */
export function tabNeedsCommerceRollup(tab: AnalyticsTab): boolean {
  return (
    tab === 'funnel' ||
    tabNeedsProductPerformance(tab)
  );
}

export const GROUP_DEFAULT_TAB: Record<AnalyticsGroup, AnalyticsTab> = {
  revenue: 'revenue-summary',
  operations: 'funnel',
  quality: 'event-governance',
  advanced: 'advanced-explore',
};

/** @deprecated Prefer `GROUP_DEFAULT_TAB` — kept for existing imports/tests. */
export const DEFAULT_TAB_BY_GROUP = GROUP_DEFAULT_TAB;

export interface GroupTabsOptions {
  readonly labelForTab: (tab: AnalyticsTab) => string;
  /** When false, omit `advanced-benchmark` from the advanced group (RBAC / feature flag). */
  readonly includeBenchmark?: boolean;
  /** When false, omit advanced-explore + advanced-benchmark from primary nav. */
  readonly includeAdvanced?: boolean;
  /**
   * Package C tier flags — when set, primary/group nav hides tabs the tenant cannot use.
   * Omit to keep legacy “show all non-advanced” behavior for callers that only pass include*.
   */
  readonly hasBasic?: boolean;
  readonly hasOperational?: boolean;
  readonly hasAdvanced?: boolean;
}

/**
 * Package C — which analytics tabs are visible for a commercial/capability tier.
 *
 * | Tab family | Gate |
 * |------------|------|
 * | Overview + Payments | FREE: analytics_basic + basic:read |
 * | Funnel / Revenue ops / Txn aggregates | PRO: analytics_overview + summary:read |
 * | Quality + Advanced | Explore: analytics_explore + detailed (+ benchmark flag) |
 */
export function isAnalyticsTabVisibleForTier(
  tab: AnalyticsTab,
  opts: {
    readonly hasBasic: boolean;
    readonly hasOperational: boolean;
    readonly hasAdvanced: boolean;
    readonly includeBenchmark?: boolean;
  },
): boolean {
  const includeBenchmark = opts.includeBenchmark !== false;
  switch (tab) {
    case 'revenue-summary':
    case 'payments':
      return opts.hasBasic;
    case 'transactions':
      // Analytics aggregates view is PRO; core Transactions list screen stays FREE elsewhere.
      return opts.hasOperational;
    case 'revenue-timeseries':
    case 'revenue-kiosks':
    case 'revenue-categories':
    case 'revenue-products':
    case 'kiosk-product-performance':
    case 'funnel':
      return opts.hasOperational;
    case 'ingest-quality':
    case 'event-governance':
    case 'advanced-explore':
      return opts.hasAdvanced;
    case 'advanced-benchmark':
      return opts.hasAdvanced && includeBenchmark;
    default: {
      const _exhaustive: never = tab;
      return _exhaustive;
    }
  }
}

/** True when the tab requires PRO operational analytics (must not background-fetch for FREE). */
export function tabRequiresOperationalAnalytics(tab: AnalyticsTab): boolean {
  return (
    tab === 'funnel' ||
    tab === 'transactions' ||
    tab === 'revenue-timeseries' ||
    tab === 'revenue-kiosks' ||
    tab === 'revenue-categories' ||
    tab === 'revenue-products' ||
    tab === 'kiosk-product-performance'
  );
}

/** True when the tab requires explore/advanced (quality + advanced tools). */
export function tabRequiresAdvancedAnalytics(tab: AnalyticsTab): boolean {
  return (
    tab === 'ingest-quality' ||
    tab === 'event-governance' ||
    tab === 'advanced-explore' ||
    tab === 'advanced-benchmark'
  );
}

export function tabToGroup(tab: AnalyticsTab): AnalyticsGroup {
  if ((REVENUE_TAB_IDS as readonly string[]).includes(tab)) {
    return 'revenue';
  }
  if ((QUALITY_TAB_IDS as readonly string[]).includes(tab)) {
    return 'quality';
  }
  if ((ADVANCED_TAB_IDS as readonly string[]).includes(tab)) {
    return 'advanced';
  }
  return 'operations';
}

export function groupToTabs(
  group: AnalyticsGroup,
  opts: GroupTabsOptions,
): AnalyticsSegmentTabItem[] {
  const includeBenchmark = opts.includeBenchmark !== false;
  const tierAware =
    opts.hasBasic !== undefined ||
    opts.hasOperational !== undefined ||
    opts.hasAdvanced !== undefined;
  const tabIds = GROUP_TAB_IDS[group].filter((tab) => {
    if (tab === 'advanced-benchmark' && !includeBenchmark) {
      return false;
    }
    if (tierAware) {
      return isAnalyticsTabVisibleForTier(tab, {
        hasBasic: opts.hasBasic === true,
        hasOperational: opts.hasOperational === true,
        hasAdvanced: opts.hasAdvanced === true,
        includeBenchmark,
      });
    }
    return true;
  });
  return tabIds.map((tab) => ({
    id: tab,
    label: opts.labelForTab(tab),
  }));
}

/** Flat primary nav list (one equal-width strip — no group chrome). */
export function primaryNavTabs(opts: GroupTabsOptions): AnalyticsSegmentTabItem[] {
  const includeBenchmark = opts.includeBenchmark !== false;
  const includeAdvanced = opts.includeAdvanced !== false;
  const tierAware =
    opts.hasBasic !== undefined ||
    opts.hasOperational !== undefined ||
    opts.hasAdvanced !== undefined;
  return PRIMARY_NAV_TAB_IDS.filter((tab) => {
    if (tab === 'advanced-explore' || tab === 'advanced-benchmark') {
      if (!includeAdvanced) {
        return false;
      }
    }
    if (tab === 'advanced-benchmark' && !includeBenchmark) {
      return false;
    }
    if (tierAware) {
      return isAnalyticsTabVisibleForTier(tab, {
        hasBasic: opts.hasBasic === true,
        hasOperational: opts.hasOperational === true,
        hasAdvanced: opts.hasAdvanced === true,
        includeBenchmark,
      });
    }
    return true;
  }).map((tab) => ({
    id: tab,
    label: opts.labelForTab(tab),
  }));
}

export function isAnalyticsGroup(value: string | null | undefined): value is AnalyticsGroup {
  return (
    value === 'revenue' ||
    value === 'operations' ||
    value === 'quality' ||
    value === 'advanced'
  );
}
